import { type FC, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useCentralizedProviderData } from '@crehana/auth';
import { useTranslation } from '@crehana/i18n';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { TextField } from '@crehana/ui';
import { get } from 'lodash';
import { useRouter } from 'next/router';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { useForm } from 'react-hook-form';

import useFeatureFlagByNameKey from '@/shared/hooks/useFeatureFlagByNameKey/useFeatureFlagByNameKey';
import adjustColorBrightness from '@/shared/utils/adjustColorBrightness';
import { EmailInput } from '@/views/Auth/components';
import { ChangePasswordForm } from '@/views/Auth/components/change-password-form';
import EmailExtraForm from '@/views/Auth/components/EmailExtraForm';
import { validations } from '@/views/Auth/constants';
import AUTH_PAGES_LOGIN_USER_MUTATION from '@/views/Auth/graphql/AuthPagesLoginUserMutation.auth.graphql';
import AUTH_PAGES_LOGIN_WITH_USERNAME_MUTATION from '@/views/Auth/graphql/AuthPagesLoginWithUsernameMutation.v5.graphql';
import type {
  AuthPagesLoginUserMutation,
  AuthPagesLoginUserMutationVariables,
} from '@/views/Auth/graphql/types/AuthPagesLoginUserMutation';
import type {
  AuthPagesLoginWithUsernameMutation,
  AuthPagesLoginWithUsernameMutationVariables,
  AuthPagesLoginWithUsernameMutation_security_login_user_SecurityMutationError as LoginWithUsernameErrorResponse,
  AuthPagesLoginWithUsernameMutation_security_login_user as LoginWithUsernameResponse,
  AuthPagesLoginWithUsernameMutation_security_login_user_LoginMutationPayload as LoginWithUsernameSuccessResponse,
} from '@/views/Auth/graphql/types/AuthPagesLoginWithUsernameMutation';
import routes from '@/views/Auth/routes';
import {
  getEmailParam,
  getNextUrl,
  getOrgLoginTitle,
} from '@/views/Auth/utils';
import getUsernameParam from '@/views/Auth/utils/getUsernameParam';

import { SocialButtonsSection } from '../SocialButtonsSection';
import type { LoginFormProps } from './types';

const isSuccessfulLogin = (
  loginUser: LoginWithUsernameResponse,
): loginUser is LoginWithUsernameSuccessResponse => 'next_url' in loginUser;

type TFormValues = {
  email: string;
  username: string;
  password: string;
};

const LoginForm: FC<React.PropsWithChildren<LoginFormProps>> = ({
  orgInfo,
}) => {
  const { t, languageCode } = useTranslation();

  // Organizations that asked for their own login headline get it from the
  // per-org map; everyone else keeps the default `FORM_TITLE` copy.
  const formTitle =
    getOrgLoginTitle(orgInfo?.slug, languageCode) ?? t('FORM_TITLE');

  const isRenderedFromReactNativeWebView = useIsRenderedFromWebView();

  const nextUrlQueryParam = getNextUrl();
  const defaultEmail = getEmailParam() as string;
  const defaultUsername = getUsernameParam() as string;
  const router = useRouter();
  const { slug } = router.query as { slug: string };

  const { currentPageOrganization } = useCentralizedProviderData();
  const learningPrimaryColor = orgInfo?.lookAndFeel?.primary?.main || undefined;
  const isCentralizedOrganization = !!currentPageOrganization?.id;
  const shouldUseLearningPrimaryColor =
    !isCentralizedOrganization && learningPrimaryColor;

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TFormValues>({
    defaultValues: {
      email: defaultEmail,
      username: defaultUsername,
      password: '',
    },
  });
  const [nextUrl, setNextUrl] = useState(nextUrlQueryParam);
  const [currentForm, setCurrentForm] = useState<
    'login-form' | 'email-extra-form' | 'change-password-form'
  >(() => {
    const renderChangePasswordForm =
      router.query.renderChangePasswordForm === 'true';

    if (renderChangePasswordForm) {
      return 'change-password-form';
    }
    return 'login-form';
  });

  const organizationId = orgInfo?.originalId;
  // Hardcoded validation for custom Brinks login
  const isBrinksOrg = organizationId === 10699;
  const { isFlagEnabled: isLoginWithUsernameEnabled } = useFeatureFlagByNameKey(
    {
      key: 'USER_LOGIN_USERNAME_METHOD',
      organizationId,
    },
  );
  // If this flag is not enabled, only social buttons will be shown
  const { isFlagEnabled: isLoginWithCredentialsEnabled } =
    useFeatureFlagByNameKey({
      key: 'SHOW_LOGIN_CREDENTIALS_FORM',
      organizationId,
    });
  const { isFlagEnabled: isPasswordStrengthEnabled } = useFeatureFlagByNameKey({
    key: 'ENABLE_PASSWORD_STRENGTH',
    organizationId,
  });

  const [loginUser] = useMutation<
    AuthPagesLoginUserMutation,
    AuthPagesLoginUserMutationVariables
  >(AUTH_PAGES_LOGIN_USER_MUTATION, {
    context: { clientName: 'auth' },
    onCompleted: data => {
      if (data?.loginUser?.success) {
        window.location.replace(data?.loginUser?.redirect || '');
      } else {
        setError('email', {
          type: 'manual',
          message: get(data?.loginUser?.messages, '[0].message'),
        });
      }
    },
  });

  const [loginWithUsername] = useMutation<
    AuthPagesLoginWithUsernameMutation,
    AuthPagesLoginWithUsernameMutationVariables
  >(AUTH_PAGES_LOGIN_WITH_USERNAME_MUTATION, {
    context: { clientName: 'v5' },
    onCompleted: data => {
      if (
        data?.security?.login_user &&
        isSuccessfulLogin(data.security.login_user)
      ) {
        const securityOptions = data.security.login_user.security_options;
        const shouldForceChangePassword = isPasswordStrengthEnabled
          ? securityOptions?.force_change_password_v2
          : securityOptions?.force_change_password;

        if (shouldForceChangePassword) {
          setNextUrl(data.security.login_user.next_url || '');
          setCurrentForm('change-password-form');
        } else if (securityOptions?.force_change_email) {
          setNextUrl(data.security.login_user.next_url || '');
          setCurrentForm('email-extra-form');
        } else {
          window.location.replace(data.security.login_user.next_url || '');
        }
      } else {
        setError('username', {
          type: 'manual',
          message: get(
            (data?.security?.login_user as LoginWithUsernameErrorResponse)
              ?.errors,
            '[0]',
          ),
        });
      }
    },
  });

  const onSubmit = (data: TFormValues) =>
    isLoginWithUsernameEnabled
      ? loginWithUsername({
          variables: {
            input: {
              organization_id: organizationId?.toString() ?? '',
              username: data.username.toLowerCase(),
              password: data.password,
              next_url: nextUrlQueryParam,
            },
          },
        })
      : loginUser({
          variables: {
            username: data.email.toLowerCase(),
            password: data.password,
            nextUrl: nextUrlQueryParam,
          },
        });

  if (currentForm === 'change-password-form') {
    return (
      <ChangePasswordForm
        organizationId={orgInfo?.originalId?.toString()}
        onSuccess={() => {
          const url = nextUrl || '/ingresar/';
          window.location.href = url;
        }}
      />
    );
  }

  if (currentForm === 'email-extra-form') {
    return <EmailExtraForm orgInfo={orgInfo} nextUrl={nextUrl} />;
  }

  if (!isLoginWithCredentialsEnabled) {
    return (
      <div className="flex flex-col md:pt-8">
        <h3 className="tw-font-h3 mb-16">{formTitle}</h3>

        <SocialButtonsSection
          organizationId={organizationId}
          nextUrl={nextUrl}
        />
      </div>
    );
  }

  // We are using the syntax [&>div>input] to target the input element inside the wrapper
  // that's because the input doesn't have a prop to override it
  const inputWrapperClassName =
    'rounded-10 [&>div>input]:tw-font-body4 md:[&>div>input]:tw-font-body3 [&>div>input]:tw-py-0 md:[&>div>input]:tw-py-1.6 [&&&]:tw-border-neutral-light-500 md:[&&&]:border-dark-gray';

  const inputLabelClassName =
    '[&>span]:tw-font-subtitle5 md:[&>span]:tw-font-subtitle4';

  return (
    <form
      className="md:pt-8 tw-flex tw-flex-col"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h3 className="tw-hidden md:tw-block tw-font-h2 tw-mb-3.2 md:tw-mb-1.6">
        {formTitle}
      </h3>

      {/* In mobile, the buttons must be at the end. That's why we are using tw-order-last here */}
      <div className="tw-order-last md:tw-order-none">
        <SocialButtonsSection
          organizationId={organizationId}
          nextUrl={nextUrl}
        />
      </div>

      {isLoginWithUsernameEnabled ? (
        <TextField
          className={`tw-mb-2.8 md:tw-mb-1.2 ${inputLabelClassName}`}
          wrapperClassName={inputWrapperClassName}
          id="username"
          name="username"
          label={t(isBrinksOrg ? 'INPUT_DNI_LABEL' : 'INPUT_USERNAME_LABEL')}
          hint={t(isBrinksOrg ? 'INPUT_DNI_HINT' : 'INPUT_USERNAME_HINT')}
          ref={register({ required: validations.required(t) })}
          disabled={isSubmitting}
          errorText={errors.username?.message}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('username', e.target.value.toLowerCase(), {
              shouldValidate: true,
            })
          }
          full
        />
      ) : (
        <EmailInput
          className={`tw-mb-2.8 md:tw-mb-1.2 ${inputLabelClassName}`}
          wrapperClassName={inputWrapperClassName}
          id="email"
          name="email"
          label={t('INPUT_EMAIL_LABEL')}
          hint={t('INPUT_EMAIL_HINT')}
          ref={register({
            required: validations.required(t),
            pattern: validations.email(t),
          })}
          defaultValue={defaultEmail}
          disabled={isSubmitting}
          errorText={errors.email?.message}
          full
          orgSlug={slug}
          hideB2CMsg
          hideB2BLoginMsg
        />
      )}

      <TextField
        className={`tw-mb-0.8 md:tw-mb-1.2 ${inputLabelClassName}`}
        wrapperClassName={inputWrapperClassName}
        id="password"
        name="password"
        type="password"
        label={t('INPUT_PASSWORD_LABEL')}
        hint={t('INPUT_PASSWORD_HINT')}
        ref={register({
          required: validations.required(t),
          minLength: validations.minLength(t),
          maxLength: validations.maxLength(t),
        })}
        disabled={isSubmitting}
        errorText={errors.password?.message}
        full
      />

      {!isRenderedFromReactNativeWebView && (
        <a
          className="block font-caption tw-text-neutral-light-700 tw-mb-3.2 md:tw-mb-2.4"
          href={routes.b2bOrgRecoverPasswordPath(slug)}
        >
          {t('FORGOT_PASSWORD')}
        </a>
      )}

      <div className="flex flex-col">
        <button
          type="submit"
          className="tw-btn-primary-m md:tw-btn-primary-l lg:w-200 tw-flex tw-justify-center"
          css={
            shouldUseLearningPrimaryColor
              ? `
            background-color: ${learningPrimaryColor};
            &:hover {
              background-color: ${adjustColorBrightness(
                learningPrimaryColor,
                30,
              )};
            }`
              : undefined
          }
        >
          {t(isSubmitting ? 'SENDING' : 'BUTTON_SUBMIT_LABEL')}
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
